import React from 'react';
import type {
  FilterItem,
  FilterSection,
  FilterValueCategory as FilterValueCategoryType,
} from '../../models/search-result';
import {
  categoryHasSelectedDescendant,
  getDefaultExpandedState,
} from './FilterSidebar.helpers';
import { DEFAULT_VISIBLE_FACET_VALUES } from './FilterSidebar.constants';
import FilterItems from './FilterItems';
import type {
  FilterSidebarExpandedState,
  FilterSidebarShowMoreState,
} from './useFilterSidebarState';

interface FilterValueCategoryProps {
  section: FilterSection;
  category: FilterValueCategoryType;
  expandedSections: FilterSidebarExpandedState;
  showMoreStates: FilterSidebarShowMoreState;
  valueCategoriesCollapsedByDefault: boolean;
  onToggleSection: (section: string) => void;
  onToggleShowMore: (section: string) => void;
  onFilterToggle: (sectionId: string, filterItem: FilterItem) => void;
  isFilterChecked: (sectionId: string, filterItem: FilterItem) => boolean;
  depth?: number;
}

const FilterValueCategory: React.FC<FilterValueCategoryProps> = ({
  section,
  category,
  expandedSections,
  showMoreStates,
  valueCategoriesCollapsedByDefault,
  onToggleSection,
  onToggleShowMore,
  onFilterToggle,
  isFilterChecked,
  depth = 0,
}) => {
  const categoryStateKey = `value-category:${section.id}:${category.id}`;
  const isExpanded =
    expandedSections[categoryStateKey] ??
    getDefaultExpandedState(
      categoryStateKey,
      valueCategoriesCollapsedByDefault,
    );

  const hasDirectFilters = category.filters.length > 0;
  const hasChildren = category.children && category.children.length > 0;
  const categoryFilter = category.filter;
  const isCategoryChecked = categoryFilter
    ? isFilterChecked(section.id, categoryFilter)
    : false;
  const isCategoryIndeterminate =
    !isCategoryChecked &&
    categoryHasSelectedDescendant(section.id, category, isFilterChecked);

  return (
    <div
      key={category.id}
      className={`filter-value-category filter-value-category-depth-${depth}`}
    >
      <div className="filter-value-category-header">
        <button
          type="button"
          className="filter-value-category-expand"
          onClick={() => onToggleSection(categoryStateKey)}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.label}`}
        >
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            ▶
          </span>
        </button>
        {categoryFilter ? (
          <label className="filter-value-category-select">
            <input
              type="checkbox"
              checked={isCategoryChecked}
              ref={(input) => {
                if (input) {
                  input.indeterminate = isCategoryIndeterminate;
                }
              }}
              onChange={() => onFilterToggle(section.id, categoryFilter)}
            />
            <span className="filter-value-category-title">
              {category.label}
            </span>
            <span className="filter-value-category-count">
              ({category.count.toLocaleString()})
            </span>
          </label>
        ) : (
          <button
            type="button"
            className="filter-value-category-toggle"
            onClick={() => onToggleSection(categoryStateKey)}
          >
            <span className="filter-value-category-title">
              {category.label}
            </span>
            <span className="filter-value-category-count">
              ({category.count.toLocaleString()})
            </span>
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="filter-value-category-items">
          {hasChildren &&
            category.children!.map((childCategory) => (
              <FilterValueCategory
                key={childCategory.id}
                section={section}
                category={childCategory}
                expandedSections={expandedSections}
                showMoreStates={showMoreStates}
                valueCategoriesCollapsedByDefault={
                  valueCategoriesCollapsedByDefault
                }
                onToggleSection={onToggleSection}
                onToggleShowMore={onToggleShowMore}
                onFilterToggle={onFilterToggle}
                isFilterChecked={isFilterChecked}
                depth={depth + 1}
              />
            ))}
          {hasDirectFilters && (
            <FilterItems
              items={category.filters}
              filterSectionId={section.id}
              showMoreStates={showMoreStates}
              onToggleShowMore={onToggleShowMore}
              onFilterToggle={onFilterToggle}
              isFilterChecked={isFilterChecked}
              limit={DEFAULT_VISIBLE_FACET_VALUES}
              stateKey={`${section.id}:${category.id}`}
              wrapperClassName="filter-value-category-filter"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FilterValueCategory;
